import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Boxes, Search, AlertTriangle, TrendingUp, SlidersHorizontal, Loader2, Info, Plus, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const UNITS = ['lb', 'oz', 'kg', 'g', 'gal', 'qt', 'l', 'ml', 'each', 'dozen', 'case'];

const AdminInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null);
  const [parItem, setParItem] = useState(null);
  const [formItem, setFormItem] = useState(null);   // ingredient being edited
  const [formOpen, setFormOpen] = useState(false);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (lowStockOnly) filters.low_stock_only = true;
      const data = await adminService.getInventory(filters);
      setInventory(data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, lowStockOnly]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  useEffect(() => {
    (async () => {
      try { setCategories(await adminService.getInventoryCategories()); }
      catch (err) {
        // Categories are optional (the form falls back to "other"); log so the
        // failure is diagnosable rather than silently swallowed.
        console.error('AdminInventory: failed to load categories', err);
      }
    })();
  }, []);

  const filterCategories = useMemo(
    () => [...new Set(inventory.map((i) => i.category))].sort(),
    [inventory]
  );
  const filtered = inventory.filter((i) => i.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const lowStockCount = inventory.filter((i) => i.is_low_stock).length;

  const handleAdjust = async ({ ingredient_id, quantity_change, reason, notes }) => {
    try {
      const res = await adminService.adjustInventory({ ingredient_id, quantity_change, reason, notes });
      toast.success(res.message || 'Inventory adjusted');
      setAdjustItem(null);
      loadInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const handleSetPar = async ({ ingredient_id, min_quantity }) => {
    try {
      const res = await adminService.setInventoryPar({ ingredient_id, min_quantity });
      toast.success(res.message || 'Par level updated');
      setParItem(null);
      loadInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set par level');
    }
  };

  const openCreate = () => { setFormItem(null); setFormOpen(true); };
  const openEdit = (item) => { setFormItem(item); setFormOpen(true); };

  const handleSaveIngredient = async (payload) => {
    try {
      if (formItem) {
        const res = await adminService.updateIngredient(formItem.id, payload);
        toast.success(res.message || 'Ingredient updated');
      } else {
        const res = await adminService.createIngredient(payload);
        toast.success(res.message || 'Ingredient added');
      }
      setFormOpen(false);
      setFormItem(null);
      loadInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save ingredient');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Boxes className="h-6 w-6 text-amber-600" /> Inventory
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {inventory.length} items{lowStockCount > 0 && <span className="text-red-600"> • {lowStockCount} low stock</span>}
          </p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={openCreate} data-testid="add-ingredient-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Ingredient
        </Button>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800" data-testid="admin-inventory-note">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        Create, edit, and adjust ingredients here. Changes use the same data the Kitchen portal manages.
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search ingredients..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="inventory-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="inventory-category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {filterCategories.map((c) => (
              <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant={lowStockOnly ? 'default' : 'outline'} onClick={() => setLowStockOnly(!lowStockOnly)}
          className={lowStockOnly ? 'bg-red-500 hover:bg-red-600' : ''} data-testid="inventory-low-stock-toggle">
          <AlertTriangle className="h-4 w-4 mr-2" /> Low Stock
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">On Hand</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Par Level</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${item.is_low_stock ? 'bg-red-50' : ''}`} data-testid={`inventory-row-${item.id}`}>
                      <td className="p-4">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        {item.supplier && <p className="text-xs text-gray-500">{item.supplier}</p>}
                      </td>
                      <td className="p-4"><Badge variant="outline" className="capitalize">{item.category}</Badge></td>
                      <td className="p-4 text-right">
                        <span className={`font-bold ${item.is_low_stock ? 'text-red-600' : 'text-gray-900'}`} data-testid={`inventory-qty-${item.id}`}>{item.current_quantity}</span>
                        <span className="text-gray-500 ml-1">{item.unit}</span>
                      </td>
                      <td className="p-4 text-right text-gray-600" data-testid={`inventory-par-${item.id}`}>{item.min_quantity} {item.unit}</td>
                      <td className="p-4">
                        {item.is_low_stock
                          ? <Badge className="bg-red-100 text-red-700">Low Stock</Badge>
                          : <Badge className="bg-green-100 text-green-700">OK</Badge>}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit ingredient" data-testid={`edit-btn-${item.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setAdjustItem(item)} title="Adjust quantity" data-testid={`adjust-btn-${item.id}`}>
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setParItem(item)} title="Set par level" data-testid={`set-par-btn-${item.id}`}>
                            <SlidersHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-12" data-testid="inventory-empty">
                  <Boxes className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No ingredients found</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust dialog */}
      <Dialog open={!!adjustItem} onOpenChange={(o) => { if (!o) setAdjustItem(null); }}>
        <DialogContent className="max-w-md" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
            <DialogDescription>{adjustItem ? `Update on-hand quantity for ${adjustItem.name}.` : ''}</DialogDescription>
          </DialogHeader>
          {adjustItem && <AdjustForm item={adjustItem} onSubmit={handleAdjust} onCancel={() => setAdjustItem(null)} />}
        </DialogContent>
      </Dialog>

      {/* Set par dialog */}
      <Dialog open={!!parItem} onOpenChange={(o) => { if (!o) setParItem(null); }}>
        <DialogContent className="max-w-md" data-testid="set-par-dialog">
          <DialogHeader>
            <DialogTitle>Set Par Level</DialogTitle>
            <DialogDescription>{parItem ? `Set the low-stock threshold for ${parItem.name}.` : ''}</DialogDescription>
          </DialogHeader>
          {parItem && <ParForm item={parItem} onSubmit={handleSetPar} onCancel={() => setParItem(null)} />}
        </DialogContent>
      </Dialog>

      {/* Create / edit ingredient dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => { if (!o) { setFormOpen(false); setFormItem(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ingredient-form-dialog">
          <DialogHeader>
            <DialogTitle>{formItem ? 'Edit Ingredient' : 'Add Ingredient'}</DialogTitle>
            <DialogDescription>
              {formItem ? `Update the details for ${formItem.name}.` : 'Add a new ingredient to inventory.'}
            </DialogDescription>
          </DialogHeader>
          <IngredientForm
            ingredient={formItem}
            categories={categories}
            onSubmit={handleSaveIngredient}
            onCancel={() => { setFormOpen(false); setFormItem(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdjustForm = ({ item, onSubmit, onCancel }) => {
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('restock');
  const [notes, setNotes] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const change = parseFloat(qty);
    if (Number.isNaN(change)) { toast.error('Enter an adjustment amount'); return; }
    onSubmit({ ingredient_id: item.id, quantity_change: change, reason, notes });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="p-3 bg-slate-50 rounded-lg">
        <p className="font-medium">{item.name}</p>
        <p className="text-sm text-gray-600">Current: {item.current_quantity} {item.unit}</p>
      </div>
      <div>
        <Label>Adjustment Amount</Label>
        <div className="flex gap-2 items-center">
          <Input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)}
            placeholder="e.g. 10 or -5" required data-testid="adjust-qty-input" />
          <span className="text-gray-600">{item.unit}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Positive to add, negative to remove.</p>
      </div>
      <div>
        <Label>Reason</Label>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="restock">Restock</SelectItem>
            <SelectItem value="production">Used in Production</SelectItem>
            <SelectItem value="waste">Waste/Spoilage</SelectItem>
            <SelectItem value="correction">Inventory Correction</SelectItem>
            <SelectItem value="count">Physical Count</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes..." />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600" data-testid="adjust-submit-btn">Adjust</Button>
      </DialogFooter>
    </form>
  );
};

const ParForm = ({ item, onSubmit, onCancel }) => {
  const [par, setPar] = useState(String(item.min_quantity ?? 0));

  const submit = (e) => {
    e.preventDefault();
    const value = parseFloat(par);
    if (Number.isNaN(value) || value < 0) { toast.error('Enter a valid par level'); return; }
    onSubmit({ ingredient_id: item.id, min_quantity: value });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="p-3 bg-slate-50 rounded-lg">
        <p className="font-medium">{item.name}</p>
        <p className="text-sm text-gray-600">Current par: {item.min_quantity} {item.unit}</p>
      </div>
      <div>
        <Label>New Par Level</Label>
        <div className="flex gap-2 items-center">
          <Input type="number" step="0.01" min="0" value={par} onChange={(e) => setPar(e.target.value)}
            required data-testid="par-input" />
          <span className="text-gray-600">{item.unit}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">The low-stock threshold. Stock at or below this triggers a low-stock state.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600" data-testid="par-submit-btn">Save Par Level</Button>
      </DialogFooter>
    </form>
  );
};

const IngredientForm = ({ ingredient, categories, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    name: ingredient?.name || '',
    category: ingredient?.category || (categories[0]?.name || 'other'),
    unit: ingredient?.unit || 'lb',
    current_quantity: ingredient?.current_quantity ?? 0,
    min_quantity: ingredient?.min_quantity ?? 0,
    cost_per_unit: ingredient?.cost_per_unit ?? 0,
    supplier: ingredient?.supplier || '',
    notes: ingredient?.notes || '',
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Ingredient name is required'); return; }
    onSubmit({
      ...form,
      name: form.name.trim(),
      current_quantity: parseFloat(form.current_quantity) || 0,
      min_quantity: parseFloat(form.min_quantity) || 0,
      cost_per_unit: parseFloat(form.cost_per_unit) || 0,
      supplier: form.supplier || null,
      notes: form.notes || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <Label>Ingredient Name</Label>
        <Input value={form.name} onChange={(e) => set({ name: e.target.value })} required data-testid="ingredient-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger data-testid="ingredient-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.length === 0 && <SelectItem value="other">other</SelectItem>}
              {categories.map((c) => (
                <SelectItem key={c.name} value={c.name}>{c.label || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Unit</Label>
          <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
            <SelectTrigger data-testid="ingredient-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Current Quantity</Label>
          <Input type="number" step="0.01" value={form.current_quantity}
            onChange={(e) => set({ current_quantity: e.target.value })} data-testid="ingredient-qty-input" />
        </div>
        <div>
          <Label>Par Level (min)</Label>
          <Input type="number" step="0.01" value={form.min_quantity}
            onChange={(e) => set({ min_quantity: e.target.value })} data-testid="ingredient-par-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Cost per Unit ($)</Label>
          <Input type="number" step="0.01" value={form.cost_per_unit}
            onChange={(e) => set({ cost_per_unit: e.target.value })} data-testid="ingredient-cost-input" />
        </div>
        <div>
          <Label>Supplier</Label>
          <Input value={form.supplier} onChange={(e) => set({ supplier: e.target.value })}
            placeholder="Optional" data-testid="ingredient-supplier-input" />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Optional" />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600" data-testid="ingredient-submit-btn">
          {ingredient ? 'Update Ingredient' : 'Add Ingredient'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default AdminInventory;